import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import kbService from '../services/kbService';
import type { GraphData } from '../types/graph';

/**
 * Domain-scoped knowledge graph, loaded once per page so the Overview concept
 * count and the Knowledge tab graph share one `GET /kb/context-graph` call.
 */
export function useDomainContextGraph(domainId: string | undefined) {
  const { t } = useTranslation();
  const [graph, setGraph] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(Boolean(domainId));
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!domainId) return;
    setLoading(true);
    setError(null);
    try {
      setGraph(await kbService.getContextGraph(domainId));
    } catch (err) {
      console.error('Failed to load domain documents graph:', err);
      setError(err instanceof Error ? err.message : t('domainDetail.graph.loadError'));
    } finally {
      setLoading(false);
    }
  }, [domainId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const conceptCount = graph ? graph.nodes.filter((node) => node.type === 'Entity').length : null;

  return { graph, loading, error, reload, conceptCount };
}
